import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";

/**
 * Overflow layout for the NPM filter bar.
 *
 * The bar must always stay on a single row. Pinned filters (and the other always-inline chrome — the
 * Saved Views button and the search input) are reserved first; the remaining optional filters are kept
 * inline in priority order for as long as they fit, and the rest collapse into a "More Filters" fly-out.
 *
 * `useFilterOverflow` measures the rendered elements and drives the split, observing the container via
 * the shared `resizeObserverManager` (never a bespoke `ResizeObserver`). The
 * fit math itself lives in the pure `computeOverflowSplit` so it can be unit-tested without a DOM.
 */

/** Cache key for the "More Filters" button's measured width. */
const BUTTON_ID = "__more-filters-button";

/** Fallback button width (px) used before the button has been measured (it only mounts once overflow occurs). */
export const OVERFLOW_BUTTON_FALLBACK_WIDTH = 132;

/** Default horizontal flex gap between filter-bar items (px). Matches the SCSS `gap` on `&__inner`. */
export const FILTER_BAR_GAP = 12;

export interface OverflowInput {
  /** Inner content width available to the row, px (the container's `clientWidth`). */
  availableWidth: number;
  /** Horizontal gap between items, px. */
  gap: number;
  /** Widths of the items that are always inline (Saved Views, pinned filters, search), px. */
  alwaysInlineWidths: number[];
  /** Overflow-candidate filters in priority order — earlier ids stay inline longer. */
  optional: Array<{ id: string; width: number }>;
  /** Measured (or fallback) width of the "More Filters" button, px. */
  buttonWidth: number;
}

export interface OverflowSplit {
  /** Optional filter ids that fit inline, in order. */
  inlineIds: string[];
  /** Optional filter ids that collapse into the fly-out, in order. */
  overflowIds: string[];
}

/**
 * Greedily assign optional filters to the inline row vs. the overflow fly-out.
 *
 * Each item "costs" its width plus one gap (a slight overestimate — there is one fewer gap than items —
 * which biases toward collapsing a touch early rather than letting the row overflow, safe under a
 * `nowrap` bar). When everything fits, no button is needed and every optional stays inline. Otherwise
 * the button's width is reserved and a *contiguous prefix* of optionals is kept inline so filter order
 * is preserved in both the inline row and the fly-out (AC #8).
 */
export function computeOverflowSplit(input: OverflowInput): OverflowSplit {
  const { availableWidth, gap, alwaysInlineWidths, optional, buttonWidth } = input;
  const cost = (width: number): number => width + gap;

  const alwaysCost = alwaysInlineWidths.reduce((sum, width) => sum + cost(width), 0);

  // Phase 1 — does the whole bar fit with no "More Filters" button at all?
  const optionalCost = optional.reduce((sum, item) => sum + cost(item.width), 0);
  if (alwaysCost + optionalCost <= availableWidth) {
    return { inlineIds: optional.map((item) => item.id), overflowIds: [] };
  }

  // Phase 2 — overflow is needed: reserve the button, then keep a contiguous inline prefix.
  const inlineIds: string[] = [];
  const overflowIds: string[] = [];
  let used = alwaysCost + cost(buttonWidth);
  let overflowing = false;
  for (const item of optional) {
    if (!overflowing && used + cost(item.width) <= availableWidth) {
      inlineIds.push(item.id);
      used += cost(item.width);
    } else {
      overflowing = true;
      overflowIds.push(item.id);
    }
  }
  return { inlineIds, overflowIds };
}

export interface UseFilterOverflowParams {
  /**
   * Ids of the items that are always inline — the Saved Views button, the pinned filters, and the search
   * input. Their widths are reserved before any optional filter is placed. Each must be registered via
   * `registerItem(id)`.
   */
  alwaysInlineIds: string[];
  /** Overflow-candidate filter ids in priority order. Each must be registered via `registerItem(id)`. */
  optionalIds: string[];
  /** Horizontal gap between items, px. Defaults to {@link FILTER_BAR_GAP}. */
  gap?: number;
}

export interface UseFilterOverflowResult {
  /** Ref callback for the row container whose `clientWidth` bounds the layout. */
  registerContainer: (element: HTMLElement | null) => void;
  /** Returns a ref callback that registers a measurable item element under `id`. */
  registerItem: (id: string) => (element: HTMLElement | null) => void;
  /** Ref callback for the "More Filters" button (mounted only while overflow is active). */
  registerButton: (element: HTMLElement | null) => void;
  /** Optional filter ids to render inline, in order. */
  inlineOptionalIds: string[];
  /** Optional filter ids to render inside the fly-out, in order. */
  overflowOptionalIds: string[];
  /** True when at least one optional filter is collapsed into the fly-out. */
  hasOverflow: boolean;
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * Measures the filter bar and returns which optional filters fit inline vs. overflow. Recomputes after
 * every render (filter labels — and therefore widths — change as selections change) and whenever the
 * container resizes.
 *
 * Width caching: an optional filter that moves into the fly-out unmounts from the measured row, so its
 * live `offsetWidth` would read 0. Widths are cached per id while the element is inline and the last
 * known value is reused when it is collapsed, so widening the viewport can bring it back.
 *
 * Observation loop safety: only the container is observed, and its `clientWidth` is driven by the page
 * layout, not by the items inside it — so moving filters in/out of the row during a callback does not
 * resize the observed element and cannot feed back into the observer.
 */
export function useFilterOverflow(params: UseFilterOverflowParams): UseFilterOverflowResult {
  const gap = params.gap ?? FILTER_BAR_GAP;

  const containerRef = useRef<HTMLElement | null>(null);
  const itemEls = useRef<Map<string, HTMLElement>>(new Map());
  const buttonEl = useRef<HTMLElement | null>(null);
  const widthCache = useRef<Map<string, number>>(new Map());

  // Latest params kept in refs so `compute` stays referentially stable (the arrays are rebuilt each render).
  const alwaysInlineIdsRef = useRef(params.alwaysInlineIds);
  const optionalIdsRef = useRef(params.optionalIds);
  alwaysInlineIdsRef.current = params.alwaysInlineIds;
  optionalIdsRef.current = params.optionalIds;

  const [split, setSplit] = useState<OverflowSplit>({ inlineIds: params.optionalIds, overflowIds: [] });

  const compute = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const availableWidth = container.clientWidth;
    // Not laid out yet (e.g. an ancestor is display:none) — keep the current split rather than
    // collapsing everything against a zero-width container.
    if (availableWidth === 0) return;

    // Refresh cached widths from the currently-mounted elements; overflowed items are absent and keep
    // their last cached width.
    itemEls.current.forEach((element, id) => {
      const width = element.offsetWidth;
      if (width > 0) widthCache.current.set(id, width);
    });
    if (buttonEl.current && buttonEl.current.offsetWidth > 0) {
      widthCache.current.set(BUTTON_ID, buttonEl.current.offsetWidth);
    }

    const widthOf = (id: string): number => widthCache.current.get(id) ?? 0;

    const next = computeOverflowSplit({
      alwaysInlineWidths: alwaysInlineIdsRef.current.map(widthOf),
      availableWidth,
      buttonWidth: widthCache.current.get(BUTTON_ID) ?? OVERFLOW_BUTTON_FALLBACK_WIDTH,
      gap,
      optional: optionalIdsRef.current.map((id) => ({ id, width: widthOf(id) })),
    });

    setSplit((prev) =>
      sameIds(prev.inlineIds, next.inlineIds) && sameIds(prev.overflowIds, next.overflowIds) ? prev : next,
    );
  }, [gap]);

  // Refs are attached during commit (before layout effects), so the container is available here.
  useLayoutEffect(() => {
    compute();
  });

  const registerContainer = useCallback(
    (element: HTMLElement | null) => {
      if (containerRef.current === element) return;
      if (containerRef.current) resizeObserverManager.unobserve(containerRef.current);
      containerRef.current = element;
      if (element) resizeObserverManager.observe(element, compute);
    },
    [compute],
  );

  // One stable ref callback per id, so re-renders don't churn refs (a fresh closure each render would make
  // React tear down and re-attach every item ref every render).
  const itemCallbacks = useRef<Map<string, (element: HTMLElement | null) => void>>(new Map());
  const registerItem = useCallback((id: string) => {
    const existing = itemCallbacks.current.get(id);
    if (existing) return existing;
    const callback = (element: HTMLElement | null): void => {
      // Keep the width cache on unmount; only the live-element map is cleared.
      if (element) itemEls.current.set(id, element);
      else itemEls.current.delete(id);
    };
    itemCallbacks.current.set(id, callback);
    return callback;
  }, []);

  const registerButton = useCallback((element: HTMLElement | null) => {
    buttonEl.current = element;
  }, []);

  return {
    hasOverflow: split.overflowIds.length > 0,
    inlineOptionalIds: split.inlineIds,
    overflowOptionalIds: split.overflowIds,
    registerButton,
    registerContainer,
    registerItem,
  };
}
